import { FvCustomEventNames } from '../../../ts/events';
import { uuidv4 } from '../../../ts/helper';

interface ComboboxOptions {
  /** The input that we append the {@link Combobox} to */
  input: HTMLInputElement;

  /**
   * The selector that targets elements that will be used to build the suggestions. On a search results page, the selector should point to the search result items.
   * @example
   *   "ul.results > li"
   */
  asyncSelector: string;

  /**
   * Minimum input length for the {@link Combobox} to show
   * @example
   *   0 // show the Combobox instantly
   *   1 // show the Combobox after at least 1 character was typed
   *   2 // show the Combobox after at least 2 characters were typed
   */
  minLength: number;

  /**
   * URL of the page to fetch, or a function that returns a URL (sync or async).
   * This is where we search for the elements by the {@link asyncSelector}. Default is the current page.
   * @example
   *   "/search"
   *   () => `/search?q=${input.value}`
   *   async () => await getDynamicUrl()
   */
  targetPage?: string | (() => string | Promise<string>);
}

/**
 * Adds a combobox to any {@link ComboboxOptions.input}. Displays autocomplete-suggestions in the frontend. Should be accessible.
 * @example
 *   const targetPage = () => {
 *     const url = new URL(formElements.form.action);
 *     url.searchParams.set('q', formElements.searchInput.value.trim());
 *     url.hash = '';
 *     return url.href;
 *   };
 *   new Combobox({
 *     input: formElements.searchInput,
 *     minLength: 3,
 *     targetPage,
 *     asyncSelector: '.searchResults-list > li'
 *   });
 */
export class Combobox {
  /** Used to abort previous fetch requests */
  private fetchController?: AbortController;
  private params: ComboboxOptions;

  /** The item that contains the {@link list} with all {@link suggestions} */
  private popup!: HTMLDivElement;

  /** The data that contains all possible suggestions */
  suggestions: string[];

  /** List contains all {@link suggestions} */
  list: HTMLUListElement;

  constructor(options: ComboboxOptions) {
    this.params = options;

    this.listen();
    this.render();
  }

  private onButtonKeydown(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    switch (e.code) {
      case 'Escape': {
        this.hideResults();
        this.params.input.focus();
        break;
      }

      case 'ArrowDown': {
        e.preventDefault();
        const next = (
          e.target as HTMLButtonElement
        ).parentElement?.nextElementSibling?.querySelector('button');
        next?.focus();
        break;
      }

      case 'ArrowUp': {
        e.preventDefault();
        const prev = (
          e.target as HTMLButtonElement
        ).parentElement?.previousElementSibling?.querySelector('button');
        prev?.focus();
        break;
      }

      default:
        break;
    }
  }

  /**
   * Will fetch all results, compare it with the current search term and display {@link matches} in the {@link list} within the {@link popup}
   */
  private async update() {
    /** Current search term */
    const search = this.normalizedValue(this.params.input.value);

    /** Only show the {@link popup} when the {@link search} has a minum length. */
    if (search.length < this.params.minLength) return;

    let targetPageUrl: string | undefined;

    if (typeof this.params.targetPage === 'function') {
      const result = this.params.targetPage();
      targetPageUrl = result instanceof Promise ? await result : result;
    } else if (typeof this.params.targetPage === 'string') {
      targetPageUrl = this.params.targetPage;
    } else {
      targetPageUrl = window.location.href;
    }

    const url = new URL(targetPageUrl || window.location.href);

    if (!targetPageUrl) url.search = ''; // clear search when using the current page
    url.searchParams.set('contentOnly', 'true'); // only fetch the content as defined in frontend/src/components/static/dom/contentOnly.twig

    this.popup.classList.add('loading');
    this.popup.setAttribute('aria-label', 'Vorschläge werden geladen');
    this.params.input.setAttribute('aria-expanded', 'true');

    // Abort previous fetch if still running
    if (this.fetchController) this.fetchController.abort();
    this.fetchController = new AbortController();

    let htmlString = '';
    try {
      htmlString = await fetch(url.href, {
        signal: this.fetchController.signal
      }).then((x) => x.text());
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Fetch was aborted, do not continue
        return;
      }
      throw err;
    }

    const tempDom = document.createElement('div');
    tempDom.innerHTML = htmlString;

    /** Elements in the {@link tempDom} that match the {@link ComboboxOptions.asyncSelector} */
    const asyncElements = Array.from(
      tempDom.querySelectorAll(this.params.asyncSelector)
    ) as HTMLElement[];

    /** find all suggestions by mapping the contents of the {@link asyncElements} */
    this.suggestions = asyncElements.map((el) => {
      const targetElement = el.querySelector(
        '.title, .result-text-headline'
      ) as HTMLElement;
      const targetText = targetElement?.innerText.trim();
      if (!targetText || !targetText.length) return;
      return targetText;
    });
    this.popup.classList.remove('loading');

    /** Matching results */
    const matches = this.searchItems(search);

    /** No need to show the {@link popup} when we have no matches or just one match that is the same as the current search */
    if (
      !matches.length ||
      (matches.length === 1 && this.normalizedValue(matches[0]) === search)
    )
      return;

    this.hideResults(true);

    /** Render the found {@link matches} */
    matches.forEach((suggestion) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.innerText = suggestion;

      button.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();

        this.params.input.value = button.innerText;
        this.params.input.setAttribute('aria-expanded', 'false');
        this.params.input.focus();
        this.popup.removeAttribute('aria-label');
        const evt = new Event(FvCustomEventNames.SearchFieldUpdateUrl);
        document.dispatchEvent(evt);
      });

      button.addEventListener('focus', () => {
        button.addEventListener('keydown', this.onButtonKeydown.bind(this));
      });

      button.addEventListener('blur', () => {
        button.removeEventListener('keydown', this.onButtonKeydown.bind(this));
      });

      li.append(button);
      this.list.append(li);
    });

    /** Tell the user that suggestions are available */
    this.popup.setAttribute(
      'aria-label',
      `${matches.length.toString()} Vorschläge verfügbar`
    );
  }

  /**
   *
   * @param search Search string
   * @returns Returns {@link suggestions}
   */
  private searchItems(search: string): Combobox['suggestions'] {
    const results = [];

    for (let i = 0; i < this.suggestions.length; i++) {
      const itemText = this.normalizedValue(this.suggestions[i]);
      if (itemText && itemText.includes(this.normalizedValue(search))) {
        results.push(this.suggestions[i]);
      }
    }

    return results;
  }

  private render() {
    /** Prepare the {@link popup} */
    this.popup = document.createElement('div');
    this.popup.id = uuidv4();
    this.popup.setAttribute('is', 'atom-combobox');
    this.popup.classList.add('loading');
    this.popup.setAttribute('role', 'group');
    this.popup.classList.add('backgroundThree');
    this.popup.setAttribute('aria-live', 'assertive');

    /** Prepare the {@link list} */
    this.list = document.createElement('ul');
    this.list.setAttribute('lang', 'de');
    this.list.classList.add('list');
    this.popup.append(this.list);

    /** Prepare the text input */
    this.params.input.setAttribute('role', 'combobox');
    this.params.input.setAttribute('aria-haspopup', 'true');
    this.params.input.setAttribute('aria-expanded', 'false');
    this.params.input.setAttribute('aria-controls', this.popup.id);
    this.params.input.setAttribute('autocomplete', 'off');
    if (!this.params.input.id?.length) this.params.input.id = uuidv4();

    this.params.input.parentElement.append(this.popup);
  }

  /**
   * Hide the {@link popup}
   */
  private hideResults(close?: boolean) {
    if (this.params.input.getAttribute('aria-expanded') === 'false') return;

    if (close) {
      this.list.innerHTML = '';
    }
  }

  /**
   * React to body click
   * @param e Mouse event
   */
  private onBodyClick(e: MouseEvent) {
    const clickInside =
      e.target === this.params.input ||
      this.popup.contains(e.target as HTMLElement);

    // Don't do anything if we clicked the input or the grid
    if (clickInside) return;

    /** hide the grid, when we click outside of any element that has to do with the {@link Combobox} */
    this.hideResults();
    this.params.input.setAttribute('aria-expanded', 'false');
  }

  /**
   * Normalize a value so it can be compared to another normalized value
   * @param value Any text
   */
  private normalizedValue(value?: string) {
    return value ? value.trim().toLowerCase() : '';
  }

  /**
   * Will run {@link update}
   * @param e Keyboard event
   */
  private onKeyUpOnInput(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    /** No value? No {@link popup}! */
    if (!this.normalizedValue(this.params.input.value).length) {
      this.hideResults(true);
      return;
    }
  }

  /**
   * Listen for keydown events on the {@link ComboboxOptions.input}
   * @param e Keyboard events
   */
  private onKeydownOnInput(e: KeyboardEvent) {
    const key = e.code;

    switch (key) {
      case 'Escape':
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        this.hideResults();
        this.params.input.setAttribute('aria-expanded', 'false');
        break;

      case 'Enter':
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        if (this.params.input.getAttribute('aria-expanded') === 'false') {
          e.preventDefault();
          this.hideResults();
          this.params.input.setAttribute('aria-expanded', 'false');
        }
        break;

      case 'ArrowDown':
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        e.preventDefault();
        this.list.querySelector('button')?.focus();
        break;

      default:
        break;
    }
  }

  /**
   * We check every x seconds and the run {@link update}
   */
  private startInteral() {
    this.update();

    let currentValue = this.normalizedValue(this.params.input.value);

    setInterval(() => {
      const nextValue = this.normalizedValue(this.params.input.value);
      if (currentValue === nextValue) return;

      currentValue = nextValue;
      this.update();
    }, 1000);
  }

  /**
   * Bind Event Listeners
   */
  private listen() {
    // On document
    document.addEventListener('click', this.onBodyClick.bind(this));

    // On input
    this.params.input.addEventListener(
      'keydown',
      this.onKeydownOnInput.bind(this)
    );
    this.params.input.addEventListener('keyup', this.onKeyUpOnInput.bind(this));
    this.params.input.addEventListener('click', () => {
      setTimeout(() => {
        if (!this.normalizedValue(this.params.input.value).length) {
          // reset
          this.hideResults(true);
        }
      }, 100);
    });
    this.params.input.addEventListener(
      'keydown',
      this.startInteral.bind(this),
      { once: true }
    );
  }
}
